import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getBookingOfferBreakdown } from './bookingOfferBreakdown';

/**
 * Generate Daily Report PDF
 * @param {Object} reportData - Report data containing bookings, staff totals, payment totals
 */
export const generateDailyReportPDF = async (reportData) => {
  try {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;

    // Header - Salon Name and Date
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text(reportData.salonName.toUpperCase(), margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.setTextColor(85, 85, 85);
    doc.text('DAILY REPORT', margin, yPos);
    yPos += 6;

    doc.setFontSize(11);
    const dateStr = reportData.date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    doc.text(`Date: ${dateStr}`, margin, yPos);
    yPos += 10;

    // Summary Section
    const totalRevenue = reportData.paymentTotals.cash.total + reportData.paymentTotals.card.total + reportData.paymentTotals.upi.total;
    const totalTransactions = reportData.paymentTotals.cash.count + reportData.paymentTotals.card.count + reportData.paymentTotals.upi.count;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(44, 62, 80);
    doc.text('SUMMARY', margin, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Total Bookings: ${reportData.bookings.length}`, margin, yPos);
    doc.text(`Total Transactions: ${totalTransactions}`, margin + 70, yPos);
    doc.text(`Total Revenue: Rs. ${totalRevenue.toFixed(2)}`, margin + 150, yPos);
    yPos += 10;

    const offerTotals = reportData.offerTotals || {
      salonOfferAmount: 0,
      ulloorOfferAmount: 0,
      otherDiscountAmount: 0,
      totalDiscountAmount: 0
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(44, 62, 80);
    doc.text('OFFER DISCOUNTS', margin, yPos);
    yPos += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Salon offers: Rs. ${offerTotals.salonOfferAmount.toFixed(2)}`, margin, yPos);
    yPos += 5;
    doc.text(`Ulloor Styles offers: Rs. ${offerTotals.ulloorOfferAmount.toFixed(2)}`, margin, yPos);
    yPos += 5;
    if (offerTotals.otherDiscountAmount > 0.5) {
      doc.text(`Other / legacy offers: Rs. ${offerTotals.otherDiscountAmount.toFixed(2)}`, margin, yPos);
      yPos += 5;
    }
    doc.setFont('helvetica', 'bold');
    doc.text(`Total discounts: Rs. ${offerTotals.totalDiscountAmount.toFixed(2)}`, margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);

    // Divider
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Bookings Table
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('ALL BOOKINGS', margin, yPos);
    yPos += 5;

    // Prepare table data
    const tableData = reportData.bookings.map((booking) => {
      const staffName = booking.staffId ? reportData.staffMap[booking.staffId] || 'Unassigned' : 'Unassigned';
      const amount = booking.gstEnabled
        ? (booking.grandTotal ?? booking.totalPrice ?? booking.servicePrice ?? 0)
        : (booking.totalPrice || booking.servicePrice || 0);
      const customerName = booking.isWalkIn ? `${booking.customerName} (Walk-in)` : booking.customerName;
      const off = getBookingOfferBreakdown(booking);
      const salonCell = off.salonOfferAmount > 0 ? `Rs. ${off.salonOfferAmount.toFixed(2)}` : '—';
      const ulloorCell = off.ulloorOfferAmount > 0 ? `Rs. ${off.ulloorOfferAmount.toFixed(2)}` : '—';

      return [
        booking.bookingTime,
        customerName,
        booking.customerPhone || 'N/A',
        booking.serviceName,
        staffName,
        (booking.paymentMode || 'Cash').toUpperCase(),
        salonCell,
        ulloorCell,
        `Rs. ${amount.toFixed(2)}${booking.gstEnabled ? ' (GST)' : ''}`
      ];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['Time', 'Customer', 'Phone', 'Service', 'Staff', 'Pay', 'Salon off.', 'Ulloor off.', 'Amount']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 10,
        halign: 'left'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [68, 68, 68]
      },
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 38, halign: 'left' },
        2: { cellWidth: 26, halign: 'left' },
        3: { cellWidth: 42, halign: 'left' },
        4: { cellWidth: 28, halign: 'left' },
        5: { cellWidth: 18, halign: 'center' },
        6: { cellWidth: 22, halign: 'right' },
        7: { cellWidth: 22, halign: 'right' },
        8: { cellWidth: 26, halign: 'right' }
      },
      margin: { left: margin, right: margin },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      }
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // Payment Summary Section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(44, 62, 80);
    doc.text('PAYMENT SUMMARY', margin, yPos);
    yPos += 7;

    // Payment summary box
    const boxX = margin;
    const boxY = yPos;
    const boxWidth = 80;
    const boxHeight = 30;

    doc.setFillColor(245, 247, 250);
    doc.rect(boxX, boxY, boxWidth, boxHeight, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(boxX, boxY, boxWidth, boxHeight, 'S');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    yPos += 7;

    doc.text(`Cash (${reportData.paymentTotals.cash.count}):`, boxX + 5, yPos);
    doc.text(`Rs. ${reportData.paymentTotals.cash.total.toFixed(2)}`, boxX + boxWidth - 5, yPos, { align: 'right' });
    yPos += 7;

    doc.text(`UPI (${reportData.paymentTotals.upi.count}):`, boxX + 5, yPos);
    doc.text(`Rs. ${reportData.paymentTotals.upi.total.toFixed(2)}`, boxX + boxWidth - 5, yPos, { align: 'right' });
    yPos += 7;

    doc.text(`Card (${reportData.paymentTotals.card.count}):`, boxX + 5, yPos);
    doc.text(`Rs. ${reportData.paymentTotals.card.total.toFixed(2)}`, boxX + boxWidth - 5, yPos, { align: 'right' });
    
    // Total line
    yPos += 2;
    doc.setLineWidth(0.5);
    doc.line(boxX + 5, yPos, boxX + boxWidth - 5, yPos);
    yPos += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Total:', boxX + 5, yPos);
    doc.text(`Rs. ${totalRevenue.toFixed(2)}`, boxX + boxWidth - 5, yPos, { align: 'right' });

    // Staff Performance Section (if available)
    if (Object.keys(reportData.staffTotals).length > 0) {
      yPos = boxY; // Start at same height as payment summary
      const staffBoxX = boxX + boxWidth + 20;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(44, 62, 80);
      doc.text('STAFF PERFORMANCE', staffBoxX, yPos - 7);

      // Staff table data
      const staffTableData = Object.entries(reportData.staffTotals)
        .sort((a, b) => b[1].total - a[1].total)
        .map(([staffName, data]) => [
          staffName,
          data.count.toString(),
          `Rs. ${data.total.toFixed(2)}`
        ]);

      // Add total row
      const totalStaffServices = Object.values(reportData.staffTotals).reduce((sum, s) => sum + s.count, 0);
      const totalStaffRevenue = Object.values(reportData.staffTotals).reduce((sum, s) => sum + s.total, 0);
      staffTableData.push(['TOTAL', totalStaffServices.toString(), `Rs. ${totalStaffRevenue.toFixed(2)}`]);

      autoTable(doc, {
        startY: yPos,
        head: [['Staff Name', 'Services', 'Revenue']],
        body: staffTableData,
        theme: 'striped',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10,
          halign: 'left'
        },
        bodyStyles: {
          fontSize: 10,
          textColor: [68, 68, 68]
        },
        columnStyles: {
          0: { cellWidth: 50, halign: 'left' },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 35, halign: 'right' }
        },
        margin: { left: staffBoxX },
        alternateRowStyles: {
          fillColor: [245, 247, 250]
        },
        // Highlight total row
        didParseCell: function(data) {
          if (data.row.index === staffTableData.length - 1 && data.section === 'body') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [230, 235, 245];
          }
        }
      });
    }

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, margin, footerY);
    doc.text('Powered by Ulloor Styles Admin', pageWidth - margin, footerY, { align: 'right' });

    // Save the PDF
    const fileName = `Daily_Report_${reportData.date.toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return true;
  } catch (error) {
    throw error;
  }
};

